import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, FileText, Award, ExternalLink, CheckCircle, Mail, Phone, Building, Shield, Home, Wrench, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import CRM from './CRM';
import TransactionDashboard from '@/components/TransactionDashboard';
import ComplianceFlow from '@/components/ComplianceFlow';
import MyLeadsTab from '@/components/MyLeadsTab';

const AgentPortal = () => {
  const [trainingModules, setTrainingModules] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentProfile, setAgentProfile] = useState({
    bio: '',
    headshot_url: ''
  });
  const user = getUser();

  useEffect(() => {
    loadPortalData();
  }, []);

  const loadPortalData = async () => {
    try {
      const [modulesRes, docsRes] = await Promise.all([
        api.get('/training/modules'),
        api.get('/documents')
      ]);
      setTrainingModules(modulesRes.data);
      setDocuments(docsRes.data);
      
      // Load agent profile if user has an agent ID
      if (user?.id) {
        try {
          const agentsRes = await api.get('/agents');
          const currentAgent = agentsRes.data.find(agent => agent.email === user.email);
          if (currentAgent) {
            setAgentProfile({
              bio: currentAgent.bio || '',
              headshot_url: currentAgent.headshot_url || ''
            });
          }
        } catch (error) {
          console.log('Could not load agent profile');
        }
      }
    } catch (error) {
      console.error('Error loading portal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeDocument = async (docId) => {
    try {
      await api.post(`/documents/${docId}/acknowledge`);
      toast.success('Document acknowledged!');
    } catch (error) {
      toast.error('Failed to acknowledge document');
    }
  };

  const complianceQuestions = [
    { id: 11, question: 'What is the fiduciary duty of a real estate agent?', type: 'multiple' },
    { id: 12, question: 'What disclosures are required in Mississippi for property sales?', type: 'multiple' },
    { id: 13, question: 'What is the Fair Housing Act and what does it prohibit?', type: 'multiple' },
    { id: 14, question: 'What are the key differences between Tennessee and Mississippi real estate law?', type: 'multiple' },
    { id: 15, question: 'What is required for a valid real estate contract?', type: 'multiple' },
    { id: 16, question: 'What are the agent\'s responsibilities regarding environmental hazards?', type: 'multiple' },
    { id: 17, question: 'What is RESPA and how does it affect real estate transactions?', type: 'multiple' },
    { id: 18, question: 'What are the licensing requirements for real estate agents in Mississippi?', type: 'multiple' },
    { id: 19, question: 'What are the licensing requirements for real estate agents in Tennessee?', type: 'multiple' },
    { id: 20, question: 'What is the role of a broker in a real estate transaction?', type: 'multiple' },
    { id: 21, question: 'What are the ethical obligations of real estate agents?', type: 'multiple' },
    { id: 22, question: 'What is commingling and why is it prohibited?', type: 'multiple' },
    { id: 23, question: 'What are the requirements for advertising real estate?', type: 'multiple' },
    { id: 24, question: 'What is required disclosure for dual agency?', type: 'multiple' },
    { id: 25, question: 'What are the penalties for license law violations?', type: 'multiple' },
    { id: 26, question: 'What is the statute of frauds as it relates to real estate?', type: 'multiple' },
    { id: 27, question: 'What are earnest money requirements and handling procedures?', type: 'multiple' },
    { id: 28, question: 'What are the requirements for property management agreements?', type: 'multiple' },
    { id: 29, question: 'What disclosures are required for lead-based paint?', type: 'multiple' },
    { id: 30, question: 'What are the continuing education requirements in Mississippi and Tennessee?', type: 'multiple' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="agent-portal">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Agent Portal</h1>
          <p className="text-gray-600">Welcome back, {user?.full_name}!</p>
        </div>

        <Tabs defaultValue="my-leads" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto">
            <TabsTrigger value="my-leads" data-testid="my-leads-tab">🎯 My Leads</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab">📊 Transactions</TabsTrigger>
            <TabsTrigger value="crm" data-testid="crm-tab">💼 CRM</TabsTrigger>
            <TabsTrigger value="training" data-testid="training-tab">Training</TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">Documents</TabsTrigger>
            <TabsTrigger value="resources" data-testid="resources-tab">Resources</TabsTrigger>
          </TabsList>

          <TabsContent value="my-leads" data-testid="my-leads-content">
            <MyLeadsTab />
          </TabsContent>

          {/* Transaction Dashboard Tab */}
          <TabsContent value="transactions" data-testid="transactions-content">
            <TransactionDashboard />
          </TabsContent>

          {/* CRM Tab */}
          <TabsContent value="crm" data-testid="crm-content">
            {/* CRM Resources Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* HubSpot CRM Signup */}
              <Card className="bg-gradient-to-br from-[#BCA480] to-[#a89470] text-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Sign Up for CRM Access
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-white/90">
                    Start managing contacts, deals, and communication with HubSpot CRM.
                  </p>
                  <Button 
                    asChild
                    className="w-full bg-white text-[#434345] hover:bg-gray-100 font-semibold"
                  >
                    <a 
                      href="https://www.hubspot.com/crm/e010a?cq_cmp=22798089764" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Sign Up for HubSpot CRM
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Email Signature Generator */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#BCA480]" />
                    Email Signature Generator
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Create a professional, branded email signature in minutes.
                  </p>
                  <Button 
                    asChild
                    variant="outline"
                    className="w-full border-[#BCA480] text-[#BCA480] hover:bg-[#BCA480] hover:text-white"
                  >
                    <a 
                      href="https://www.hubspot.com/email-signature-generator" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Create Your Email Signature
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* CRM Dashboard */}
            <CRM />
          </TabsContent>

          {/* Training Tab */}
          <TabsContent value="training" data-testid="training-content">
            <Card>
              <CardHeader>
                <CardTitle>🎓 Bespoke Realty Fast Start - 6 Week Training</CardTitle>
                <CardDescription>
                  Your complete onboarding program to become a successful Bespoke agent
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Enroll Button */}
                <div className="mb-6 p-4 bg-gradient-to-r from-[#BCA480] to-[#a89470] rounded-lg text-white">
                  <h3 className="text-xl font-bold mb-2">Ready to Start Your Journey?</h3>
                  <p className="mb-4">Enroll in the complete 6-week Fast Start program</p>
                  <Button 
                    asChild
                    className="bg-white text-[#434345] hover:bg-gray-100"
                  >
                    <a 
                      href="https://bespokeacadademy.thinkific.com/products/courses/bespoke-realty-fast-start" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Enroll in Bespoke Academy
                    </a>
                  </Button>
                </div>

                {/* Training Modules */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Week 1 */}
                  <div className="border rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#BCA480]">Week 1</span>
                      <span className="px-3 py-1 bg-[#BCA480] text-white text-xs rounded-full">Foundation</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Welcome to Your New Level</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Introduction to Bespoke Realty culture, values, and what it takes to succeed.
                    </p>
                    <Button 
                      asChild
                      size="sm"
                      className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                    >
                      <a 
                        href="https://bespokeacadademy.thinkific.com/courses/take/bespoke-realty-fast-start/lessons/70811571-lesson-1-introduction-to-bespoke-realty" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Start Week 1
                      </a>
                    </Button>
                  </div>

                  {/* Week 2 */}
                  <div className="border rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#BCA480]">Week 2</span>
                      <span className="px-3 py-1 bg-[#BCA480] text-white text-xs rounded-full">Professional</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">From Licensed to Legit</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Transform from having a license to being a legitimate professional agent.
                    </p>
                    <Button 
                      asChild
                      size="sm"
                      className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                    >
                      <a 
                        href="https://bespokeacadademy.thinkific.com/courses/take/bespoke-realty-fast-start/lessons/70811574-lesson-1-welcome-to-week-2" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Start Week 2
                      </a>
                    </Button>
                  </div>

                  {/* Week 3 */}
                  <div className="border rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#BCA480]">Week 3</span>
                      <span className="px-3 py-1 bg-[#BCA480] text-white text-xs rounded-full">Business</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Leads, Language, and Listings</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Master lead generation as a daily habit and build your pipeline.
                    </p>
                    <Button 
                      asChild
                      size="sm"
                      className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                    >
                      <a 
                        href="https://bespokeacadademy.thinkific.com/courses/take/bespoke-realty-fast-start/lessons/70811576-lesson-1-lead-generation-is-a-daily-habit" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Start Week 3
                      </a>
                    </Button>
                  </div>

                  {/* Week 4 */}
                  <div className="border rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#BCA480]">Week 4</span>
                      <span className="px-3 py-1 bg-[#BCA480] text-white text-xs rounded-full">Marketing</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Marketing With Intention and Visibility</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Market yourself strategically as a professional agent with purpose.
                    </p>
                    <Button 
                      asChild
                      size="sm"
                      className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                    >
                      <a 
                        href="https://bespokeacadademy.thinkific.com/courses/take/bespoke-realty-fast-start/lessons/70811578-lesson-1-marketing-yourself-as-a-professional-agent" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Start Week 4
                      </a>
                    </Button>
                  </div>

                  {/* Week 5 */}
                  <div className="border rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#BCA480]">Week 5</span>
                      <span className="px-3 py-1 bg-[#BCA480] text-white text-xs rounded-full">Transactions</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Transaction Confidence and Professional Leadership</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Understand contracts without fear and lead transactions confidently.
                    </p>
                    <Button 
                      asChild
                      size="sm"
                      className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                    >
                      <a 
                        href="https://bespokeacadademy.thinkific.com/courses/take/bespoke-realty-fast-start/lessons/70811580-lesson-1-understanding-the-contract-without-fear" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Start Week 5
                      </a>
                    </Button>
                  </div>

                  {/* Week 6 */}
                  <div className="border rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#BCA480]">Week 6</span>
                      <span className="px-3 py-1 bg-[#BCA480] text-white text-xs rounded-full">Mastery</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Real Conversations & Real Scenarios</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Practice scripts and handle real conversations in real estate situations.
                    </p>
                    <Button 
                      asChild
                      size="sm"
                      className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                    >
                      <a 
                        href="https://bespokeacadademy.thinkific.com/courses/take/bespoke-realty-fast-start/lessons/71651522-lesson-1-scripts-real-conversations-in-real-estate" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Start Week 6
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Additional Resources */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">📚 Training Tips</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Complete one week before moving to the next</li>
                    <li>• Take notes and practice what you learn</li>
                    <li>• Reach out to your broker with questions</li>
                    <li>• Apply lessons immediately in your business</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Documents / Compliance Flow Tab */}
          <TabsContent value="documents" data-testid="documents-content">
            <ComplianceFlow />
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" data-testid="resources-content">
            {/* Bio and Headshot Upload */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Update Your Profile</CardTitle>
                <CardDescription>Keep your bio and headshot current for the website</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Bio Section */}
                  <div>
                    <Label htmlFor="agent-bio" className="text-base font-semibold mb-2 block">Agent Bio</Label>
                    <p className="text-sm text-gray-600 mb-3">Write a brief professional bio that will appear on your agent profile (150-300 words recommended)</p>
                    <Textarea
                      id="agent-bio"
                      value={agentProfile.bio}
                      onChange={(e) => setAgentProfile({...agentProfile, bio: e.target.value})}
                      rows={6}
                      placeholder="Example: I am a dedicated real estate professional serving Mississippi and Tennessee with over 5 years of experience. My passion is helping families find their perfect home..."
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">{agentProfile.bio.length} characters</p>
                  </div>

                  {/* Headshot Section */}
                  <div>
                    <Label htmlFor="agent-headshot" className="text-base font-semibold mb-2 block">Professional Headshot URL</Label>
                    <p className="text-sm text-gray-600 mb-3">Upload your headshot to Google Drive or Dropbox and paste the public link here</p>
                    <Input
                      id="agent-headshot"
                      type="url"
                      value={agentProfile.headshot_url}
                      onChange={(e) => setAgentProfile({...agentProfile, headshot_url: e.target.value})}
                      placeholder="https://drive.google.com/..."
                      className="w-full"
                    />
                    {agentProfile.headshot_url && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold mb-2">Preview:</p>
                        <img 
                          src={agentProfile.headshot_url} 
                          alt="Headshot preview" 
                          className="w-32 h-32 rounded-lg object-cover border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <Button 
                    onClick={async () => {
                      try {
                        await api.patch(`/agents/${user.id}/profile`, agentProfile);
                        toast.success('Profile updated successfully!');
                      } catch (error) {
                        toast.error('Failed to update profile. Please try again.');
                      }
                    }}
                    className="bg-[#BCA480] hover:bg-[#a89470]"
                  >
                    Save Profile Updates
                  </Button>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Your updated bio and headshot will appear on the &quot;Our Agents&quot; page within 24 hours of submission.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Agent Resources</CardTitle>
                <CardDescription>Quick access to tools and materials</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Bespoke Color Palette */}
                <div className="bg-gradient-to-br from-[#BCA480]/10 to-white p-6 rounded-lg mb-6">
                  <h3 className="font-bold text-xl mb-4">Bespoke Realty Brand Colors</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="w-full h-24 rounded-lg mb-2" style={{backgroundColor: '#BCA480'}}></div>
                      <p className="font-semibold text-sm">Muted Gold</p>
                      <p className="text-xs text-gray-600">#BCA480</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-24 rounded-lg mb-2" style={{backgroundColor: '#434345'}}></div>
                      <p className="font-semibold text-sm">Charcoal Gray</p>
                      <p className="text-xs text-gray-600">#434345</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-24 rounded-lg mb-2 border" style={{backgroundColor: '#000000'}}></div>
                      <p className="font-semibold text-sm">Black</p>
                      <p className="text-xs text-gray-600">#000000</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-24 rounded-lg mb-2 border" style={{backgroundColor: '#FFFFFF'}}></div>
                      <p className="font-semibold text-sm">White</p>
                      <p className="text-xs text-gray-600">#FFFFFF</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a 
                    href="https://bespokerealty.qbstores.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow" 
                    data-testid="order-apparel-link"
                  >
                    <h3 className="font-semibold mb-2">Order Bespoke Realty Apparel</h3>
                    <p className="text-sm text-gray-600">Branded shirts and professional attire</p>
                    <ExternalLink className="h-4 w-4 inline ml-1" />
                  </a>
                  <a 
                    href="https://www.markful.com/bespoke-realty" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow" 
                    data-testid="order-cards-link"
                  >
                    <h3 className="font-semibold mb-2">Order Business Cards</h3>
                    <p className="text-sm text-gray-600">Professional marketing materials</p>
                    <ExternalLink className="h-4 w-4 inline ml-1" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Preferred Vendors */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#BCA480]" />
                  Preferred Vendors
                </CardTitle>
                <CardDescription>Trusted partners for your transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  
                  {/* Home Inspectors */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <Home className="h-5 w-5 text-[#BCA480]" />
                      <h4 className="font-semibold text-[#434345]">Home Inspectors</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-sm">Scooter Holiday</p>
                        <p className="text-xs text-gray-600">National Property Inspections</p>
                        <a href="tel:901-497-0943" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-497-0943
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Terrell</p>
                        <p className="text-xs text-gray-600">Veracity Home Inspector</p>
                        <a href="tel:901-236-3691" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-236-3691
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Pest Inspections */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <Shield className="h-5 w-5 text-[#BCA480]" />
                      <h4 className="font-semibold text-[#434345]">Pest Inspections</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-sm">Action Pest Control</p>
                        <a href="tel:662-895-4200" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 662-895-4200
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Rainbow Pest Control</p>
                        <a href="tel:901-275-0204" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-275-0204
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Lenders */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <Building className="h-5 w-5 text-[#BCA480]" />
                      <h4 className="font-semibold text-[#434345]">Lenders</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-sm">Tammy Kennedy</p>
                        <p className="text-xs text-gray-600">Nations Lending</p>
                        <a href="tel:901-649-3793" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-649-3793
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Steve Blake</p>
                        <p className="text-xs text-gray-600">Prime Lending</p>
                        <a href="tel:901-238-4468" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-238-4468
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Shelia Middleton</p>
                        <p className="text-xs text-gray-600">Cadence Bank</p>
                        <a href="tel:901-409-2035" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-409-2035
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Title Companies */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <FileText className="h-5 w-5 text-[#BCA480]" />
                      <h4 className="font-semibold text-[#434345]">Title Companies</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-sm">Guardian Title</p>
                        <p className="text-xs text-gray-600">Southaven</p>
                        <a href="tel:662-262-1800" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 662-262-1800
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Amanda Mitchell</p>
                        <p className="text-xs text-gray-600">Titan Title</p>
                        <a href="tel:850-348-2057" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 850-348-2057
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Excel Title and Escrow</p>
                        <a href="tel:901-779-7006" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-779-7006
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Home Warranty */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <CheckCircle className="h-5 w-5 text-[#BCA480]" />
                      <h4 className="font-semibold text-[#434345]">Home Warranty</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-sm">Christy Stokes</p>
                        <p className="text-xs text-gray-600">Choice Home Warranty</p>
                        <a href="tel:901-616-2285" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-616-2285
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Structural Engineer */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <Wrench className="h-5 w-5 text-[#BCA480]" />
                      <h4 className="font-semibold text-[#434345]">Structural Engineer</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-sm">Poe Engineer</p>
                        <a href="tel:901-860-4763" className="text-xs text-[#BCA480] hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> 901-860-4763
                        </a>
                      </div>
                    </div>
                  </div>

                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AgentPortal;
